import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GlassCard, KpiTile as BrandKpiTile, EnforcedText } from '../components/shared';
import LoadingSpinner from '../components/LoadingSpinner';
import ComparisonPill from '../components/ComparisonPill';
import { ArrowLeft, Edit2, Save, X, Plus } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PricingDetail = () => {
  const { skuId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sku, setSku] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [computed, setComputed] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [editMode, setEditMode] = useState({ bom: false, install: false });
  const [editedBOM, setEditedBOM] = useState([]);
  const [editedInstall, setEditedInstall] = useState([]);
  const { user } = useAuth();
  
  // Check if user has admin role
  const isAdmin = user?.role === 'super_admin' || user?.role === 'program_admin';

  useEffect(() => {
    if (skuId) {
      fetchDetail();
    }
  }, [skuId]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/pricing/variant/${skuId}`);
      setSku(response.data.sku);
      setCompetitors(response.data.competitor_quotes || []);
      setComputed(response.data.computed);
      setEditedBOM(response.data.sku.bom_components);
      setEditedInstall(response.data.sku.install_costs);
    } catch (error) {
      console.error('Error fetching pricing detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveBOM = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/pricing/variant/${skuId}/bom`, editedBOM);
      setEditMode({ ...editMode, bom: false });
      fetchDetail(); // Refresh to get updated computed values
    } catch (error) {
      console.error('Error saving BOM:', error);
      alert('Failed to save BOM');
    }
  };

  const saveInstall = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/pricing/variant/${skuId}/install`, editedInstall);
      setEditMode({ ...editMode, install: false });
      fetchDetail(); // Refresh to get updated computed values
    } catch (error) {
      console.error('Error saving installation costs:', error);
      alert('Failed to save installation costs');
    }
  };

  const formatCurrency = (value) => {
    return `₹${value?.toLocaleString() || '0'}`;
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading pricing details..." />;
  }

  if (!sku) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Pricing variant not found</p>
        <button
          onClick={() => navigate('/pricing')}
          className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg"
        >
          Back to Matrix
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/pricing')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900">{sku._id}</h1>
              <span className={`px-3 py-1 text-sm font-medium rounded-lg ${
                sku.type === 'AC' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
              }`}>
                {sku.type} • {sku.power_kw} kW
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              <EnforcedText>{sku.connectors.join(', ')} • {sku.notes}</EnforcedText>
            </p>
          </div>
        </div>
        <ComparisonPill 
          status={computed?.comparison_status}
          text={computed?.comparison_text}
          size="lg"
        />
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {isAdmin && (
          <BrandKpiTile
            title="G4G Total Cost"
            value={formatCurrency(computed?.g4g_total_cost)}
            subcopy={`BOM: ${formatCurrency(computed?.cost_bom_sum)} • Install: ${formatCurrency(computed?.cost_install_sum)}`}
            accentBar="primary"
            definition="Our complete cost"
          />
        )}
        <BrandKpiTile
          title="Market Average"
          value={formatCurrency(computed?.market_avg_total)}
          subcopy={`${competitors.length} competitor quotes`}
          accentBar="accent"
          definition="Market average price"
        />
        <BrandKpiTile
          title="Target Price"
          value={formatCurrency(sku.selling_band.target)}
          delta={`${formatCurrency(sku.selling_band.min)} - ${formatCurrency(sku.selling_band.max)}`}
          accentBar="green"
          definition="Our selling price band"
        />
        {isAdmin && (
          <BrandKpiTile
            title="Target Margin"
            value={formatCurrency(computed?.target_margin)}
            delta={`${computed?.target_margin_percent.toFixed(1)}%`}
            accentBar="bharat"
            definition="Profit at target price"
          />
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-4">
          {['summary', ...(isAdmin ? ['bom', 'install'] : []), 'competitors'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'summary' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cost Breakdown - Only for Admin */}
          {isAdmin && (
            <GlassCard title="Cost Breakdown" subtitle="Complete cost structure">
              <div className="space-y-3">
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">BOM Components</span>
                    <span className="font-bold text-purple-700">{formatCurrency(computed?.cost_bom_sum)}</span>
                  </div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">Installation</span>
                    <span className="font-bold text-blue-700">{formatCurrency(computed?.cost_install_sum)}</span>
                  </div>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">Contingency (5%)</span>
                    <span className="font-bold text-yellow-700">{formatCurrency(computed?.contingency_auto)}</span>
                  </div>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border-2 border-green-300">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-900">Total G4G Cost</span>
                    <span className="font-bold text-lg text-green-700">{formatCurrency(computed?.g4g_total_cost)}</span>
                  </div>
                </div>
              </div>
            </GlassCard>
          )}

          {/* Market Comparison */}
          <GlassCard title="Market Comparison" subtitle="Competitive positioning">
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-700">Market Avg Hardware</span>
                  <span className="font-bold">{formatCurrency(computed?.market_avg_hw)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">Market Avg Install</span>
                  <span className="font-bold">{formatCurrency(computed?.market_avg_install)}</span>
                </div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-900">Market Total</span>
                  <span className="font-bold text-lg text-blue-700">{formatCurrency(computed?.market_avg_total)}</span>
                </div>
              </div>
              <div className="p-4 bg-gradient-to-br from-purple-50 to-green-50 rounded-lg border-2 border-purple-300">
                <div className="text-center">
                  <p className="text-xs text-gray-600 mb-1">Price Difference</p>
                  <p className={`text-2xl font-bold ${
                    computed?.target_margin >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {computed?.comparison_text}
                  </p>
                  <ComparisonPill 
                    status={computed?.comparison_status}
                    text={computed?.comparison_text}
                    size="md"
                  />
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      )}

      {activeTab === 'bom' && isAdmin && (
        <GlassCard 
          title="Bill of Materials" 
          subtitle={`Total: ${formatCurrency(computed?.cost_bom_sum)}`}
          action={
            <button
              onClick={() => editMode.bom ? saveBOM() : setEditMode({ ...editMode, bom: true })}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg flex items-center gap-2 ${
                editMode.bom
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {editMode.bom ? <><Save size={14} /> Save</> : <><Edit2 size={14} /> Edit</>}
            </button>
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Component</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Cost (₹)</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Notes</th>
                </tr>
              </thead>
              <tbody>
                {editedBOM.map((component, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="px-4 py-3">{component.name}</td>
                    <td className="px-4 py-3 text-right">
                      {editMode.bom ? (
                        <input
                          type="number"
                          value={component.avg_cost}
                          onChange={(e) => {
                            const updated = [...editedBOM];
                            updated[idx].avg_cost = parseFloat(e.target.value) || 0;
                            setEditedBOM(updated);
                          }}
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                        />
                      ) : (
                        formatCurrency(component.avg_cost)
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{component.notes}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr>
                  <td className="px-4 py-3 font-bold">Total BOM Cost</td>
                  <td className="px-4 py-3 text-right font-bold text-purple-600">
                    {formatCurrency(editedBOM.reduce((sum, c) => sum + c.avg_cost, 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </GlassCard>
      )}

      {activeTab === 'install' && isAdmin && (
        <GlassCard 
          title="Installation Costs" 
          subtitle={`Total: ${formatCurrency(computed?.cost_install_sum)}`}
          action={
            <button
              onClick={() => editMode.install ? saveInstall() : setEditMode({ ...editMode, install: true })}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg flex items-center gap-2 ${
                editMode.install
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {editMode.install ? <><Save size={14} /> Save</> : <><Edit2 size={14} /> Edit</>}
            </button>
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Item</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Cost (₹)</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Notes</th>
                </tr>
              </thead>
              <tbody>
                {editedInstall.map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="px-4 py-3">{item.name}</td>
                    <td className="px-4 py-3 text-right">
                      {editMode.install && item.name !== "Contingency 5%" ? (
                        <input
                          type="number"
                          value={item.avg_cost}
                          onChange={(e) => {
                            const updated = [...editedInstall];
                            updated[idx].avg_cost = parseFloat(e.target.value) || 0;
                            setEditedInstall(updated);
                          }}
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                        />
                      ) : item.name === "Contingency 5%" ? (
                        <span className="text-gray-500">Auto</span>
                      ) : (
                        formatCurrency(item.avg_cost)
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{item.notes}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr>
                  <td className="px-4 py-3 font-bold">Total Install Cost</td>
                  <td className="px-4 py-3 text-right font-bold text-purple-600">
                    {formatCurrency(editedInstall.filter(i => i.name !== "Contingency 5%").reduce((sum, c) => sum + c.avg_cost, 0))}
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-bold text-yellow-700">+ Contingency (5%)</td>
                  <td className="px-4 py-3 text-right font-bold text-yellow-700">
                    {formatCurrency(computed?.contingency_auto)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </GlassCard>
      )}

      {activeTab === 'competitors' && (
        <GlassCard 
          title="Competitor Quotes" 
          subtitle={`${competitors.length} quotes tracked`}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Brand</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Model</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">List Price</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Install Price</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Total</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Source</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">City</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map((quote) => (
                  <tr key={quote._id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{quote.brand}</td>
                    <td className="px-4 py-3">{quote.model}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(quote.list_price)}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(quote.install_price)}</td>
                    <td className="px-4 py-3 text-right font-semibold">{formatCurrency(quote.total_price)}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{quote.source}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{quote.city}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {competitors.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>No competitor quotes available for this variant</p>
            </div>
          )}
        </GlassCard>
      )}
    </div>
  );
};

export default PricingDetail;
